// Locale-aware formatters (currency, number, date) — no FX conversion.
// The currency CODE is always preserved as stored; only the display format changes.
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

const LOCALE_TAG = { en: "en-US", fr: "fr-FR" };

export function useLocaleFormat() {
  const { i18n } = useTranslation();
  const lng = i18n.language?.split("-")[0] === "fr" ? "fr" : "en";
  const tag = LOCALE_TAG[lng];

  return useMemo(() => ({
    locale: lng,
    formatCurrency(amount, currency) {
      if (amount == null || isNaN(Number(amount))) return "—";
      const code = (currency || "USD").toUpperCase();
      try {
        return new Intl.NumberFormat(tag, {
          style: "currency",
          currency: code,
          maximumFractionDigits: 2,
        }).format(Number(amount));
      } catch {
        return `${Number(amount).toLocaleString(tag)} ${code}`;
      }
    },
    formatNumber(value, opts = {}) {
      if (value == null || isNaN(Number(value))) return "—";
      try {
        return new Intl.NumberFormat(tag, opts).format(Number(value));
      } catch {
        return String(value);
      }
    },
    formatDate(iso, opts = { year: "numeric", month: "short", day: "numeric" }) {
      if (!iso) return "—";
      try {
        return new Intl.DateTimeFormat(tag, opts).format(new Date(iso));
      } catch {
        return String(iso);
      }
    },
    formatBytes(bytes) {
      if (!bytes) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      let i = 0;
      let n = Number(bytes);
      while (n >= 1024 && i < units.length - 1) {
        n /= 1024;
        i++;
      }
      const formatted = new Intl.NumberFormat(tag, { maximumFractionDigits: 1 }).format(n);
      return `${formatted} ${units[i]}`;
    },
    /**
     * Locale-aware relative-time formatter ("il y a 2 heures" / "2 hours ago").
     * Falls back to a short ISO date when the input is older than 30 days.
     */
    formatRelative(iso) {
      if (!iso) return "—";
      try {
        const then = new Date(iso).getTime();
        const diffSec = Math.round((Date.now() - then) / 1000);
        const rtf = new Intl.RelativeTimeFormat(tag, { numeric: "auto" });
        const abs = Math.abs(diffSec);
        if (abs < 60) return rtf.format(-Math.sign(diffSec) * abs, "second");
        if (abs < 3600) return rtf.format(-Math.sign(diffSec) * Math.round(abs / 60), "minute");
        if (abs < 86400) return rtf.format(-Math.sign(diffSec) * Math.round(abs / 3600), "hour");
        if (abs < 86400 * 30) return rtf.format(-Math.sign(diffSec) * Math.round(abs / 86400), "day");
        return new Intl.DateTimeFormat(tag, { year: "numeric", month: "short", day: "numeric" }).format(new Date(iso));
      } catch {
        return String(iso);
      }
    },
  }), [tag, lng]);
}
